import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Database, Plus, Trash2, Edit3, Save, X, Search,
  HelpCircle, CheckCircle, ToggleLeft, FolderOpen,
  ChevronDown, ChevronUp, Upload, Tag, Hash, ArrowLeft
} from 'lucide-react';

const QUESTION_TYPES = [
  { value: 'qcm', label: 'QCM (1 reponse)', icon: HelpCircle },
  { value: 'vrai_faux', label: 'Vrai / Faux', icon: ToggleLeft },
  { value: 'qcm_multiple', label: 'QCM Multiple', icon: CheckCircle },
];

const QuestionBankPage = ({ user }) => {
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTheme, setShowNewTheme] = useState(false);
  const [newThemeName, setNewThemeName] = useState('');
  const [newThemeDesc, setNewThemeDesc] = useState('');
  const [editingTheme, setEditingTheme] = useState(null);
  const [showNewQuestion, setShowNewQuestion] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const loadThemes = useCallback(async () => {
    try {
      const r = await axios.get('/foad/question-bank/themes');
      setThemes(r.data);
    } catch { toast.error('Erreur chargement themes'); }
    finally { setLoading(false); }
  }, []);

  const loadQuestions = useCallback(async (themeId) => {
    try {
      const url = themeId ? `/foad/question-bank/questions?theme_id=${themeId}` : '/foad/question-bank/questions';
      const r = await axios.get(url);
      setQuestions(r.data);
    } catch { toast.error('Erreur chargement questions'); }
  }, []);

  useEffect(() => { loadThemes(); }, [loadThemes]);
  useEffect(() => { loadQuestions(selectedTheme); }, [selectedTheme, loadQuestions]);

  const createTheme = async () => {
    if (!newThemeName.trim()) return;
    try {
      await axios.post('/foad/question-bank/themes', { name: newThemeName, description: newThemeDesc });
      toast.success('Theme cree');
      setNewThemeName(''); setNewThemeDesc(''); setShowNewTheme(false);
      loadThemes();
    } catch { toast.error('Erreur'); }
  };

  const deleteTheme = async (themeId, name) => {
    if (!window.confirm(`Supprimer le theme "${name}" et toutes ses questions ?`)) return;
    try {
      await axios.delete(`/foad/question-bank/themes/${themeId}`);
      toast.success('Theme supprime');
      if (selectedTheme === themeId) setSelectedTheme(null);
      loadThemes(); loadQuestions(selectedTheme);
    } catch { toast.error('Erreur'); }
  };

  const deleteQuestion = async (qId) => {
    try {
      await axios.delete(`/foad/question-bank/questions/${qId}`);
      toast.success('Question supprimee');
      loadQuestions(selectedTheme);
    } catch { toast.error('Erreur'); }
  };

  const filteredQuestions = questions.filter(q =>
    q.texte.toLowerCase().includes(search.toLowerCase())
  );

  const selectedThemeData = themes.find(t => t.theme_id === selectedTheme);

  return (
    <div className="space-y-6" data-testid="question-bank-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/foad')} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors" data-testid="back-to-foad-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" data-testid="qbank-title">
              <Database className="h-7 w-7 text-indigo-600" /> Banque de Questions
            </h1>
            <p className="text-slate-500 text-sm mt-1">Organisez vos questions par theme et alimentez vos quiz</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Themes sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Themes</h3>
              <Button size="sm" variant="ghost" className="h-7 text-indigo-600" onClick={() => setShowNewTheme(!showNewTheme)} data-testid="add-theme-btn">
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>

            {showNewTheme && (
              <div className="mb-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200 space-y-2">
                <input value={newThemeName} onChange={e => setNewThemeName(e.target.value)} placeholder="Nom du theme..." className="w-full border rounded-lg px-3 py-1.5 text-sm" data-testid="new-theme-name" autoFocus />
                <input value={newThemeDesc} onChange={e => setNewThemeDesc(e.target.value)} placeholder="Description..." className="w-full border rounded-lg px-3 py-1.5 text-sm" />
                <div className="flex gap-2">
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white h-7 text-xs" onClick={createTheme} data-testid="save-theme-btn">Creer</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowNewTheme(false)}>Annuler</Button>
                </div>
              </div>
            )}

            {/* All questions */}
            <button onClick={() => setSelectedTheme(null)}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-1 ${!selectedTheme ? 'bg-indigo-100 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid="theme-all">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2"><FolderOpen className="h-4 w-4" /> Toutes</span>
                <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">{themes.reduce((s, t) => s + (t.question_count || 0), 0)}</span>
              </div>
            </button>

            {themes.map(t => (
              <div key={t.theme_id} className="group relative">
                <button onClick={() => setSelectedTheme(t.theme_id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-1 ${selectedTheme === t.theme_id ? 'bg-indigo-100 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}
                  data-testid={`theme-${t.theme_id}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2"><Tag className="h-3.5 w-3.5" /> {t.name}</span>
                    <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">{t.question_count || 0}</span>
                  </div>
                </button>
                <button onClick={() => deleteTheme(t.theme_id, t.name)}
                  className="absolute right-1 top-2.5 p-1 rounded text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`delete-theme-${t.theme_id}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}

            {loading && <div className="text-center py-4"><div className="animate-spin h-5 w-5 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto" /></div>}
          </div>
        </div>

        {/* Questions area */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl border">
            {/* Questions header */}
            <div className="p-4 border-b flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  {selectedThemeData ? selectedThemeData.name : 'Toutes les questions'}
                </h3>
                <p className="text-xs text-slate-500">{filteredQuestions.length} question(s)</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="border rounded-lg pl-9 pr-3 py-1.5 text-sm w-56" data-testid="qbank-search" />
                </div>
                {selectedTheme && (
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => { setEditingQuestion(null); setShowNewQuestion(true); }} data-testid="add-question-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
                  </Button>
                )}
              </div>
            </div>

            {/* New/Edit Question form */}
            {(showNewQuestion || editingQuestion) && selectedTheme && (
              <QuestionForm
                themeId={selectedTheme}
                question={editingQuestion}
                onSaved={() => { setShowNewQuestion(false); setEditingQuestion(null); loadQuestions(selectedTheme); loadThemes(); }}
                onCancel={() => { setShowNewQuestion(false); setEditingQuestion(null); }}
              />
            )}

            {/* Questions list */}
            <div className="divide-y max-h-[60vh] overflow-y-auto">
              {filteredQuestions.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Database className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">{selectedTheme ? 'Aucune question dans ce theme' : 'Selectionnez un theme ou recherchez'}</p>
                </div>
              ) : (
                filteredQuestions.map(q => {
                  const qt = QUESTION_TYPES.find(t => t.value === q.type) || QUESTION_TYPES[0];
                  const QIcon = qt.icon;
                  const themeName = themes.find(t => t.theme_id === q.theme_id)?.name || '';
                  return (
                    <div key={q.bank_question_id} className="p-4 hover:bg-slate-50 group" data-testid={`bank-q-${q.bank_question_id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <QIcon className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                            <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">{qt.label}</span>
                            {!selectedTheme && <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{themeName}</span>}
                          </div>
                          <p className="text-sm text-slate-800 font-medium">{q.texte}</p>
                          {q.options?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {q.options.map((o, i) => (
                                <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${o.correct ? 'bg-green-100 text-green-700 font-semibold' : 'bg-slate-100 text-slate-500'}`}>
                                  {o.texte}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-3">
                          <Button variant="ghost" size="sm" className="h-7 text-slate-500" onClick={() => { setEditingQuestion(q); setShowNewQuestion(false); }} data-testid={`edit-q-${q.bank_question_id}`}>
                            <Edit3 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 text-red-500" onClick={() => deleteQuestion(q.bank_question_id)} data-testid={`delete-q-${q.bank_question_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============= QUESTION FORM =============
const QuestionForm = ({ themeId, question, onSaved, onCancel }) => {
  const [type, setType] = useState(question?.type || 'qcm');
  const [texte, setTexte] = useState(question?.texte || '');
  const [options, setOptions] = useState(question?.options || [{ texte: '', correct: false }, { texte: '', correct: false }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (type === 'vrai_faux') {
      setOptions([{ texte: 'Vrai', correct: true }, { texte: 'Faux', correct: false }]);
    } else if (type === 'qcm' || type === 'qcm_multiple') {
      if (options.length < 2 || (question?.type !== type)) {
        setOptions([{ texte: '', correct: false }, { texte: '', correct: false }]);
      }
    }
  }, [type]);

  const save = async () => {
    if (!texte.trim()) { toast.error('Intitule requis'); return; }
    if ((type === 'qcm' || type === 'qcm_multiple') && !options.some(o => o.correct)) { toast.error('Au moins une reponse correcte requise'); return; }
    setSaving(true);
    try {
      const payload = { theme_id: themeId, texte, type, options, points: 1, pairs: [], blanks: [], explication: '' };
      if (question?.bank_question_id) {
        await axios.put(`/foad/question-bank/questions/${question.bank_question_id}`, payload);
        toast.success('Question mise a jour');
      } else {
        await axios.post('/foad/question-bank/questions', payload);
        toast.success('Question ajoutee');
      }
      onSaved();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const updOpt = (i, field, val) => {
    const u = [...options];
    u[i] = { ...u[i], [field]: val };
    if (field === 'correct' && type === 'qcm') {
      u.forEach((o, j) => { if (j !== i) u[j] = { ...o, correct: false }; });
    }
    setOptions(u);
  };

  return (
    <div className="p-4 border-b bg-indigo-50/50">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          {QUESTION_TYPES.map(qt => (
            <button key={qt.value} onClick={() => setType(qt.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${type === qt.value ? 'bg-indigo-600 text-white' : 'bg-white border text-slate-600 hover:bg-indigo-50'}`}
              data-testid={`qform-type-${qt.value}`}>
              <qt.icon className="h-3.5 w-3.5" /> {qt.label}
            </button>
          ))}
        </div>
        <input value={texte} onChange={e => setTexte(e.target.value)} placeholder="Intitule de la question..." className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="qform-texte" autoFocus />
        
        {(type === 'qcm' || type === 'qcm_multiple' || type === 'vrai_faux') && (
          <div className="space-y-2 ml-4">
            {type === 'vrai_faux' && <p className="text-xs text-slate-500">Selectionnez la bonne reponse :</p>}
            {options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <button type="button" onClick={() => updOpt(i, 'correct', !o.correct)}
                  className={`h-5 w-5 ${type === 'qcm' || type === 'vrai_faux' ? 'rounded-full' : 'rounded'} border-2 flex items-center justify-center flex-shrink-0 ${o.correct ? 'bg-green-500 border-green-500 text-white' : 'border-slate-300'}`}
                  data-testid={`qform-opt-correct-${i}`}>
                  {o.correct && <CheckCircle className="h-3 w-3" />}
                </button>
                {type === 'vrai_faux' ? (
                  <span className="text-sm font-medium">{o.texte}</span>
                ) : (
                  <input value={o.texte} onChange={e => updOpt(i, 'texte', e.target.value)} placeholder={`Reponse ${i + 1}...`} className="flex-1 border rounded px-2 py-1.5 text-sm" data-testid={`qform-opt-text-${i}`} />
                )}
                {type !== 'vrai_faux' && options.length > 2 && (
                  <Button variant="ghost" size="sm" className="h-7 text-slate-400" onClick={() => setOptions(options.filter((_, j) => j !== i))}>
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
            {type !== 'vrai_faux' && (
              <Button variant="ghost" size="sm" className="text-indigo-600 text-xs" onClick={() => setOptions([...options, { texte: '', correct: false }])}>
                + Ajouter reponse
              </Button>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 pt-2">
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={save} disabled={saving} data-testid="qform-save">
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Enregistrement...' : question ? 'Mettre a jour' : 'Ajouter'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>Annuler</Button>
        </div>
      </div>
    </div>
  );
};

export default QuestionBankPage;
